// Pure helpers for the "Rate your experience" page (copy rules, gating, date
// formatting). Kept free of React so they are trivially unit-testable and so
// the copy lives in one place.

// Mirrors app.VisitRatingCommentMinLen / MaxLen on the server (runes there,
// UTF-16 units here — close enough for a hint; the server is authoritative).
export const COMMENT_MIN_LENGTH = 10;
export const COMMENT_MAX_LENGTH = 2000;
// Below this many characters remaining the hint flips to "N characters left".
export const COMMENT_LEFT_WARNING_THRESHOLD = 100;
// Display-only review title derived from the comment (never stored).
export const REVIEW_TITLE_MAX_LENGTH = 40;

export const RATING_STATUS_OK = "ok";
export const RATING_STATUS_EXPIRED = "expired";
export const RATING_STATUS_INVALID = "invalid";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// "2026-08-15" -> "August 15, 2026". The server sends a calendar date, so this
// never goes through Date() — parsing "YYYY-MM-DD" as UTC and printing it in a
// US-evening timezone would shift the visit to the day before.
export function formatVisitDate(isoDate) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate || "");
  if (!m) {
    return "";
  }
  const month = MONTHS[Number(m[2]) - 1];
  const day = Number(m[3]);
  if (!month || !day) {
    return "";
  }
  return `${month} ${day}, ${m[1]}`;
}

// "Based on your {productLabel} visit on {visitDate} with {doctorName}",
// dropping whichever pieces the server could not fill in.
export function visitSummaryLine({ productLabel, visitDate, doctorName } = {}) {
  const product = (productLabel || "").trim();
  const date = formatVisitDate(visitDate);
  const doctor = (doctorName || "").trim();

  let line = product ? `Based on your ${product} visit` : "Based on your visit";
  if (date) {
    line += ` on ${date}`;
  }
  if (doctor) {
    line += ` with ${doctor}`;
  }
  return line;
}

export function commentPlaceholderForStars(stars) {
  const n = Number(stars) || 0;
  if (n <= 2) {
    return "What went wrong this time? How can we improve? Remember to be honest, helpful, and constructive!";
  }
  if (n === 3) {
    return "What did you like or dislike? What is Kick doing well, or how can we improve? Remember to be honest, helpful, and constructive!";
  }
  return "What made your experience great? What is Kick doing well? Remember to be honest, helpful, and constructive!";
}

export function trimmedCommentLength(comment) {
  return (comment || "").trim().length;
}

// The hint row under the textarea:
//   - "Type N more characters." until the trimmed comment reaches the minimum
//   - "N characters left" once fewer than 100 of the 2000 max remain
//   - null otherwise (row hidden)
export function commentHint(comment) {
  const trimmed = trimmedCommentLength(comment);
  if (trimmed < COMMENT_MIN_LENGTH) {
    const more = COMMENT_MIN_LENGTH - trimmed;
    return {
      kind: "more",
      text: `Type ${more} more character${more === 1 ? "" : "s"}.`,
    };
  }
  const left = COMMENT_MAX_LENGTH - (comment || "").length;
  if (left < COMMENT_LEFT_WARNING_THRESHOLD) {
    return {
      kind: "left",
      text: `${left} character${left === 1 ? "" : "s"} left`,
    };
  }
  return null;
}

export function canSubmitRating({ stars, comment } = {}) {
  return (
    (Number(stars) || 0) >= 1 &&
    trimmedCommentLength(comment) >= COMMENT_MIN_LENGTH
  );
}

// Map selected tag codes back to the server-supplied labels (unknown codes are
// shown as-is rather than dropped, so an edited rating never loses a chip).
export function tagLabelsFor(codes, tags) {
  const byCode = {};
  (tags || []).forEach((t) => {
    if (t && t.code) {
      byCode[t.code] = t.label || t.code;
    }
  });
  return (codes || []).map((code) => byCode[code] || code);
}

// Trustpilot shows a title above the review and fills it from the comment's
// first words when the reviewer leaves it blank. We never store a title; this
// derives one for display only, and returns "" for short comments so the card
// does not print the same sentence twice.
export function deriveReviewTitle(comment) {
  const text = (comment || "").replace(/\s+/g, " ").trim();
  if (text.length <= REVIEW_TITLE_MAX_LENGTH) {
    return "";
  }
  let cut = text.slice(0, REVIEW_TITLE_MAX_LENGTH);
  const lastSpace = cut.lastIndexOf(" ");
  if (lastSpace >= REVIEW_TITLE_MAX_LENGTH / 2) {
    cut = cut.slice(0, lastSpace);
  }
  cut = cut.replace(/[\s,.;:!?-]+$/, "");
  return `${cut}…`;
}
